import cn from 'classnames';
import { Children, cloneElement, ReactElement } from 'react';
import { IconType } from 'react-icons/lib';

type Children = ReactElement | ReactElement[] | any;

export interface FormStepperProps {
    onStep?: (step: number) => void;
    activeStep?: number;
    className?: string;
    children?: Children;
    isView?: boolean;
}

export interface FormStepperMainProps extends FormStepperProps {
    title?: string;
    connector?: ReactElement;
}

export interface FormStepperLabelProps extends FormStepperProps {
    StepIcon: IconType;
}

export interface FormStepperStepProps extends FormStepperProps {
    step: number;
    label: string;
    lastStep?: boolean;
}

const FormStepper = {
    Main: ({ activeStep, className, children, isView = false, onStep }: FormStepperMainProps) => {
        return (
            <div
                className={cn(
                    `flex flex-wrap w-full h-full items-center bg-brand-background py-4 text-brand-neutral-200 gap-3 px-2 sm:px-4`,
                    className
                )}>
                {Children.map(children, (child, index) =>
                    cloneElement(child, {
                        activeStep,
                        lastStep: Children.count(children) - 1 === index,
                        isView,
                        onStep
                    })
                )}
            </div>
        );
    },

    Step: ({ step, label, activeStep, lastStep, isView, onStep }: FormStepperStepProps) => (
        <li
            className={cn('flex items-center gap-3 rtl:space-x-reverse cursor-pointer', {
                'text-brand-primary-light': step === activeStep
            })}
            onClick={() => onStep?.(step)}>
            <div className="flex items-center gap-2">
                {!isView && (
                    <span
                        className={cn('flex items-center justify-center w-5 h-5 text-xs border rounded-full shrink-0', {
                            'border-brand-primary-light': step === activeStep
                        })}>
                        {step + 1}
                    </span>
                )}
                <h3 className="font-medium text-sm">{label}</h3>
            </div>
            {!lastStep && !isView && (
                <svg
                    className="w-3 h-3 rtl:rotate-180"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 12 10">
                    <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m7 9 4-4-4-4M1 9l4-4-4-4"
                    />
                </svg>
            )}

            {!lastStep && isView && <div className="">/</div>}
        </li>
    )
};

export default FormStepper;
